<template>
  <div>
    <div
      v-for="(prod, prodIndex) in data.products"
      :key="prodIndex"
    >
      <div>
        <VSelect
          v-model="prod.networkId"
          :rules="{ required: true }"
          :label="$t('massBalance.modalStep.network')"
          itemText="name"
          itemValue="id"
          outlined
          :items="getAvailableNetworks(prod)"
          @input="debouncedNetworkInput(prod.networkId)"
          @change="emitChange"
        />
        <div class="productInfo">
          <VSelect
            v-model="prod.siteProduct"
            :rules="{ required: true }"
            :label="$t('massBalance.modalStep.productName')"
            itemText="name"
            itemValue="id"
            class="w-input"
            outlined
            :items="getAvailableProductsByNetwork(prod).listAvailableProducts"
            @input="debouncedProductInput(prod)"
            @change="emitChange"
          />
          <VTextField
            v-model="prod.ppm"
            type="number"
            :label="$t(`massBalance.modalStep.${massBalanceType === STEAM ? 'ppmBoiler' : 'ppm'}`)"
            :rules="{ required: true }"
            class="w-input"
            outlined
            @input="onInput"
            @change="emitChange"
          />
          <v-switch
            v-model="prod.activeConcentration"
            class="mt-0 ml-3"
            :label="$t('massBalance.modalStep.concentration')"
            @change="onInput"
          />
        </div>
        <VAutocomplete
          v-model="prod.componentIds"
          :label="$t('massBalance.modalStep.component')"
          :items="listComponentByNetwork[prod.networkId][prod.siteProduct]"
          outlined
          :multiple="true"
          :rules="{ required: true }"
          @input="onInput"
          @change="emitChange"
        />
      </div>
      <div
        v-if="data.products.length > 1"
        class="delStyleContent"
        @click="removeProductData(prodIndex)"
      >
        <div class="delStyleContentDel">
          <v-icon
            color="white"
            size="20"
          >
            delete
          </v-icon>
        </div>
        <span class="ml-2">{{ $t('forms.labels.delete') }}</span>
      </div>
      <div class="actionStyle">
        <div
          class="subBarre"
        />
        <div
          v-if="prodIndex === data.products.length - 1"
          class="addStyle"
          @click="addProductData"
        >
          <div class="addStyleContent">
            <v-icon
              color="white"
              size="20"
            >
              add
            </v-icon>
          </div>
          <div>
            {{ $t('massBalance.add') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import { cloneDeep, debounce, isEmpty } from 'lodash';
import { STEAM } from '@/aquassist-shared/constants/types/network';

export default {
    name: 'ProductsForm',
    components: {
        VAutocomplete,
        VSelect,
        VTextField,
    },
    props: {
        mutableValue: {
            type: Object,
            default: null,
        },
        massBalanceType: {
            type: String,
            default: '',
        },
        massBalanceSelected: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            data: {
                products: [],
            },
            listNetwork: [],
            listProductByNetwork: {},
            listComponentByNetwork: {},
            debouncedNetworkInput: debounce(this.onNetworkInput, 300),
            debouncedProductInput: debounce(this.onProductInput, 300),
        };
    },
    computed: {
        ...mapGetters([
            'massBalanceData',
            'site',
        ]),
        STEAM() {
            return STEAM;
        },
    },
    created() {
        this.getNetworkByFlowmeter();
        this.unsubscribe = this.$store.subscribe(mutation => {
            if (mutation.type === 'setFlowmeterNetwork') {
                this.getNetworkByFlowmeter();
            }
        });
    },
    mounted() {
        this.onInput();
        this.emitChange();
    },
    beforeDestroy() {
        this.unsubscribe();
    },
    methods: {
        ...mapActions({
            setproducts: 'setproducts',
        }),
        addProductData() {
            this.data.products.push({
                networkId: '',
                siteProduct: '',
                ppm: null,
                activeConcentration: false,
                componentIds: [],
                device: '',
            });
            this.emitChange();
        },
        removeProductData(index) {
            const products = cloneDeep(this.data.products);
            products.splice(index, 1);
            this.data = {
                ...this.data,
                products,
            };
            this.emitChange();
        },
        getAvailableNetworks(product) {
            const selectedNetworks = [];
            this.data.products.forEach(prod => {
                const alreadyPushed = selectedNetworks.find(sn => sn.networkId === prod.networkId);
                if (prod.networkId && !alreadyPushed) {
                    selectedNetworks.push(prod);
                }
            });
            if (selectedNetworks.length < 1) {
                return this.listNetwork;
            }
            const toBeRemoved = [];
            selectedNetworks.forEach(sn => {
                const { availableProducts } = this.getAvailableProductsByNetwork(sn);
                if (availableProducts.length < 1) {
                    toBeRemoved.push(sn);
                }
            });
            const selectedNetworksId = toBeRemoved.map(sn => sn.networkId);
            let availableNetworks = this.listNetwork.filter(an => !selectedNetworksId.includes(an.id));
            const toBeListed = this.listNetwork.filter(ln => selectedNetworksId.includes(ln.id));
            if (product.networkId) {
                availableNetworks = [...availableNetworks, ...toBeListed];
            }
            return availableNetworks;
        },
        getAvailableProductsByNetwork(product) {
            if (!product.networkId) {
                return { listAvailableProducts: [], availableProducts: [] };
            }
            const selectedProducts = this.data.products.map(sp => sp.siteProduct);
            const availableProducts = this.listProductByNetwork[product.networkId].filter(ap => !selectedProducts.includes(ap.id));
            const listAvailableProducts = [...availableProducts];
            const selectedProduct = this.listProductByNetwork[product.networkId].find(lpn => lpn.id === product.siteProduct);
            if (product.siteProduct) {
                listAvailableProducts.push(selectedProduct);
            }
            return { listAvailableProducts, availableProducts };
        },
        getNetworkByFlowmeter() {
            this.data.products = [
                {
                    networkId: '',
                    siteProduct: '',
                    ppm: null,
                    activeConcentration: false,
                    componentIds: [],
                    device: '',
                },
            ];
            const modeEdit = !isEmpty(this.massBalanceSelected);
            const { allNetworks } = this.massBalanceData.flowMeters;
            this.listNetwork = allNetworks;
            const netIds = allNetworks.map(n => n.id);
            // On init, to avoid crash
            this.listComponentByNetwork[''] = {};
            netIds.forEach(id => {
                this.listProductByNetwork = {
                    ...this.listProductByNetwork,
                    [id]: [],
                };
                this.listComponentByNetwork = {
                    ...this.listComponentByNetwork,
                    [id]: {},
                };
            });
            if (modeEdit) {
                this.data.products = cloneDeep(this.massBalanceSelected.products).filter(p => netIds.includes(p.networkId));
                this.data.products.forEach(p => {
                    this.onNetworkInput(p.networkId);
                    this.onProductInput(p);
                });
            }
        },
        async onNetworkInput(networkId) {
            const { data } = await this.$rq.getMassBalanceProductByNetwork(this.site.id, networkId);
            this.listProductByNetwork = {
                ...this.listProductByNetwork,
                [networkId]: data.map(p => ({
                    name: p.name,
                    id: p.id,
                    device: p.device,
                })),
            };
            this.onInput();
        },
        async onProductInput(product) {
            const { data } = await this.$rq.getMassBalanceProductByComponent(
                this.site.id,
                product.networkId,
                product.siteProduct,
            );
            const device = this.listProductByNetwork[product.networkId].find(p => p.id === product.siteProduct)?.device;
            if (device) {
                this.data.products = this.data.products.map(p => (p.siteProduct === product.siteProduct ? { ...p, device } : p));
            }
            this.listComponentByNetwork = {
                ...this.listComponentByNetwork,
                [product.networkId]: {
                    ...this.listComponentByNetwork[product.networkId],
                    [product.siteProduct]: data.map(p => ({
                        name: p.name,
                        id: p.id,
                    })),
                },
            };

            this.onInput();
        },
        validateProductsInput() {
            let isValid = true;
            this.data.products.forEach(p => {
                isValid = isValid && !!p.networkId && !!p.siteProduct && !!p.ppm && p.componentIds.length > 0;
            });
            return isValid;
        },
        emitChange() {
            this.$emit('inputChange', this.validateProductsInput);
        },
        onInput() {
            const data = this.data.products;
            const summaryData = [];
            this.data.products.forEach(p => {
                const productList = this.listProductByNetwork[p.networkId];
                const siteProduct = productList ? productList.find(n => n.id === p.siteProduct) : { siteProduct: { name: '', id: '' } };
                summaryData.push({
                    network: this.listNetwork.find(n => n.id === p.networkId),
                    siteProduct,
                    ppm: p.ppm,
                    activeConcentration: p.activeConcentration,
                    selectedComponents: p.componentIds,
                    componentIds: Object.values(this.listComponentByNetwork[p.networkId]).flat().filter(n => p.componentIds.includes(n.id)),
                });
            });
            this.setproducts({ products: { data, summaryData } });
        },
    },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/massBalanceStyle.scss";
</style>
